import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Video, Play, Lock } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CourseContentPage = () => {
  const { user } = useApp();
  const [courseData, setCourseData] = useState({ access: null, videos: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchCourseContent();
  }, [user, navigate]);

  const fetchCourseContent = async () => {
    try {
      const response = await axios.get(`${API}/course/videos`, { withCredentials: true });
      setCourseData(response.data);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = activeCategory === 'all' 
    ? courseData.videos 
    : courseData.videos.filter(v => v.category === activeCategory);

  const tierLabels = { entry: 'Entry', standard: 'Standard', pro: 'Pro' };
  const tierColors = { 
    entry: 'bg-blue-500/20 text-blue-400 border-blue-500/30', 
    standard: 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30', 
    pro: 'bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/30' 
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!courseData.access) {
    return (
      <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="course-no-access">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-neutral-800 flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/20">
            <Lock size={40} className="text-[#D4AF37]/50" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>No Course Access</h1>
          <p className="text-neutral-400 mb-8">
            You don't have access to the Mix Course yet. Purchase a tier to unlock exclusive video content.
          </p>
          <button onClick={() => navigate('/services')} className="btn-primary">
            View Course Tiers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="course-content-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-white" style={{ fontFamily: 'Syne' }}>
              Mix <span className="gradient-text">Course</span>
            </h1>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${tierColors[courseData.access]}`}>
                {tierLabels[courseData.access]} Access
              </span>
              <span className="text-neutral-500">{courseData.videos.length} videos available</span>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        {courseData.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === 'all' 
                  ? 'bg-[#D4AF37] text-black' 
                  : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-[#D4AF37]'
              }`}
            >
              All Videos
            </button>
            {courseData.categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${
                  activeCategory === cat 
                    ? 'bg-[#D4AF37] text-black' 
                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-[#D4AF37]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Video Grid */}
        {filteredVideos.length === 0 ? (
          <div className="text-center py-20">
            <Video size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
            <p className="text-neutral-500">No videos in this category yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map(video => (
              <div key={video.video_id} className="glass-card overflow-hidden group" data-testid={`video-${video.video_id}`}>
                <div className="aspect-video relative bg-neutral-900">
                  {video.thumbnail_url ? (
                    <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video size={48} className="text-[#D4AF37]/30" />
                    </div>
                  )}
                  <a
                    href={video.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center glow-primary">
                      <Play size={28} fill="black" stroke="black" className="ml-1" />
                    </div>
                  </a>
                  <span className={`absolute top-3 right-3 px-2 py-1 rounded text-xs font-medium border ${tierColors[video.tier]}`}>
                    {tierLabels[video.tier]}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-bold mb-1 text-white" style={{ fontFamily: 'Syne' }}>{video.title}</h3>
                  <p className="text-sm text-neutral-500 mb-2 line-clamp-2">{video.description}</p>
                  <div className="flex items-center justify-between text-xs text-neutral-500">
                    <span className="capitalize">{video.category}</span>
                    {video.duration_minutes > 0 && <span>{video.duration_minutes} min</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseContentPage;
